import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ReactSelect } from "@/components/ui/react-select";
import { PackageCheck, Loader2 } from "lucide-react";
import warrantyApi from "@/api/warrantyApi";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";

const FulfilReplacementForm = ({ claim, onOpenChange, onDone }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);

  const targetProductId =
    claim?.replacement_product_id ||
    claim?.replacement_product?.id ||
    claim?.product_id ||
    claim?.product?.id;

  const targetStoreId = claim?.store_id || currentStore?.id;

  const [selectedProduct, setSelectedProduct] = useState(
    claim?.replacement_product || claim?.product || null,
  );
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const initialPrice =
    claim?.replacement_unit_price ||
    claim?.sale_item?.unit_price ||
    claim?.product?.price ||
    0;
  const [unitPrice, setUnitPrice] = useState(String(initialPrice));

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      return String(dateStr).split("T")[0];
    } catch {
      return "";
    }
  };

  const [warrantyStartDate, setWarrantyStartDate] = useState(() => {
    return formatDateForInput(claim?.warranty_start_date) || new Date().toISOString().split("T")[0];
  });
  const [warrantyEndDate, setWarrantyEndDate] = useState(() => {
    return formatDateForInput(claim?.warranty_end_date);
  });

  // Fetch available products
  const { data: productsData = [] } = useQuery({
    queryKey: ["products-fulfil-list"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.PRODUCTS, {
        params: { per_page: 100 },
      });
      return res.data?.data || res.data || [];
    },
  });

  const activeProductId = selectedProduct?.id || targetProductId;

  // Available serials picker
  const { data: availableSerials = [], isLoading: isLoadingSerials } = useQuery({
    queryKey: ["available-serials", activeProductId, targetStoreId],
    queryFn: async () => {
      if (!activeProductId || !targetStoreId) return [];
      const res = await warrantyApi.getAvailableSerials(activeProductId, targetStoreId);
      return res.data?.data || res.data || [];
    },
    enabled: Boolean(activeProductId && targetStoreId),
  });

  // Available batches picker
  const { data: availableBatches = [], isLoading: isLoadingBatches } = useQuery({
    queryKey: ["available-batches", activeProductId, targetStoreId],
    queryFn: async () => {
      if (!activeProductId || !targetStoreId) return [];
      try {
        const res = await warrantyApi.getAvailableBatches(activeProductId, targetStoreId);
        return res.data?.data || res.data || [];
      } catch {
        return [];
      }
    },
    enabled: Boolean(activeProductId && targetStoreId),
  });

  const activeProduct = selectedProduct || claim?.replacement_product || claim?.product;

  // A product can only be tracked by serial OR batch at one time, never both
  const trackingType = useMemo(() => {
    if (activeProduct?.tracking_type === "serial" || Boolean(activeProduct?.is_serialized)) {
      return "serial";
    }
    if (activeProduct?.tracking_type === "standard") {
      return "batch";
    }
    if (activeProduct?.tracking_type === "none") {
      return "none";
    }

    if (claim?.serial_number || claim?.replacement_serial_number) return "serial";
    if (claim?.inventory_batch_id) return "batch";

    if (availableSerials?.length > 0 && availableBatches?.length === 0) return "serial";
    if (availableBatches?.length > 0 && availableSerials?.length === 0) return "batch";
    if (availableSerials?.length > 0) return "serial";
    if (availableBatches?.length > 0) return "batch";

    return "none";
  }, [
    activeProduct?.tracking_type,
    activeProduct?.is_serialized,
    claim?.serial_number,
    claim?.replacement_serial_number,
    claim?.inventory_batch_id,
    availableSerials?.length,
    availableBatches?.length,
  ]);

  const isSerialTracked = trackingType === "serial";
  const isBatchTracked = trackingType === "batch";

  const claimSerialNumber = claim?.serial_number;
  const filteredSerials = useMemo(() => {
    if (!Array.isArray(availableSerials)) return [];
    const origSn = String(claimSerialNumber || "").trim().toLowerCase();
    return availableSerials.filter((s) => {
      const sn = String(typeof s === "string" ? s : s?.serial_number || s?.serial || "");
      if (!sn || !sn.trim()) return false;
      if (origSn && sn.trim().toLowerCase() === origSn) return false;
      return true;
    });
  }, [availableSerials, claimSerialNumber]);

  const fulfilMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        replacement_product_id: activeProductId || null,
        replacement_serial_id: isSerialTracked ? (selectedSerial?.id || null) : null,
        inventory_batch_id: isBatchTracked ? (selectedBatch?.id || null) : null,
        unit_price: unitPrice ? parseFloat(unitPrice) : null,
        warranty_start_date: warrantyStartDate || null,
        warranty_end_date: warrantyEndDate || null,
      };

      const res = await warrantyApi.fulfil(claim.id, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success("Replacement fulfilled successfully. Item issued to customer.");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }

      const result = data?.data || data;
      if (result?.replacement_sale) {
        const sale = result.replacement_sale;
        const total = parseFloat(sale.total || sale.grand_total || 0);
        if (total > 0 || sale.payment_status === "pending") {
          toast.info(
            `Replacement sale #${sale.invoice_number || sale.id} created. Amount due: $${total.toFixed(2)}.`,
            {
              action: {
                label: "Go to Payment",
                onClick: () => navigate(`/pos?sale_id=${sale.id}&payment=true`),
              },
              duration: 10000,
            },
          );
        }
      }

      onOpenChange(false);
      if (onDone) onDone(data);
    },
    onError: (error) => {
      const msg = error.response?.data?.message || "Failed to fulfil replacement";
      toast.error(msg);
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    fulfilMutation.mutate();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <PackageCheck className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle>Fulfil Replacement</DialogTitle>
            <DialogDescription>
              Issue replacement unit from newly arrived stock for claim #{claim.claim_number}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        {/* Target Product */}
        <div className="space-y-1.5">
          <Label className="text-xs font-medium">Replacement Product</Label>
          <ReactSelect
            value={
              selectedProduct
                ? { value: selectedProduct.id, label: selectedProduct.name }
                : null
            }
            onChange={(opt) => {
              if (!opt) return setSelectedProduct(null);
              const p = productsData.find((item) => item.id === opt.value);
              setSelectedProduct(p || { id: opt.value, name: opt.label });
              setSelectedSerial(null);
              setSelectedBatch(null);
            }}
            options={productsData.map((p) => ({
              value: p.id,
              label: `${p.name} ${p.sku ? `(${p.sku})` : ""}`,
            }))}
          />
        </div>

        {/* Tracking: Serial OR Batch (Mutually Exclusive) */}
        {isSerialTracked && (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Available Serial *</Label>
            <ReactSelect
              isClearable
              isLoading={isLoadingSerials}
              placeholder={
                isLoadingSerials
                  ? "Loading serials in stock..."
                  : filteredSerials.length > 0
                    ? "Select serialized unit from available stock..."
                    : "No serial units available in stock"
              }
              value={
                selectedSerial
                  ? {
                    value: selectedSerial.id,
                    label: selectedSerial.serial_number,
                    status: selectedSerial.status,
                  }
                  : null
              }
              onChange={(opt) => {
                if (!opt) return setSelectedSerial(null);
                const s = filteredSerials.find((item) => item.id === opt.value);
                setSelectedSerial(s || { id: opt.value, serial_number: opt.label });
              }}
              options={filteredSerials.map((s) => ({
                value: s.id,
                label: s.serial_number || s.code || s.id,
                status: s.status_info?.label || s.status,
              }))}
              formatOptionLabel={(option) => (
                <div className="flex justify-between items-center w-full">
                  <span className="font-mono text-xs">{option.label}</span>
                  {option.status && (
                    <Badge
                      variant="outline"
                      className="text-[10px] h-4 text-emerald-600 bg-emerald-50 border-emerald-200"
                    >
                      {option.status}
                    </Badge>
                  )}
                </div>
              )}
            />
            <div className="text-[11px] text-muted-foreground flex justify-between items-center px-0.5">
              <span>{filteredSerials.length} unit(s) available in this store</span>
              {claim.serial_number && <span className="font-mono">Original SN: {claim.serial_number}</span>}
            </div>
          </div>
        )}

        {isBatchTracked && (
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Inventory Batch</Label>
            <ReactSelect
              isClearable
              isLoading={isLoadingBatches}
              placeholder={isLoadingBatches ? "Loading batches..." : "Select batch..."}
              value={
                selectedBatch
                  ? {
                    value: selectedBatch.id,
                    label: `${selectedBatch.batch_number} (Qty: ${selectedBatch.quantity_available})`,
                  }
                  : null
              }
              onChange={(opt) => {
                if (!opt) return setSelectedBatch(null);
                const b = availableBatches.find((item) => item.id === opt.value);
                setSelectedBatch(b || { id: opt.value });
              }}
              options={availableBatches.map((b) => ({
                value: b.id,
                label: `${b.batch_number || b.id} - Available: ${b.quantity_available || b.quantity}`,
              }))}
            />
            {availableBatches.length === 0 && !isLoadingBatches && (
              <div className="text-[11px] text-muted-foreground px-0.5">
                No active batches found in store
              </div>
            )}
          </div>
        )}

        {/* Unit price */}
        <div className="space-y-1.5">
          <Label htmlFor="fulfil_unit_price" className="text-xs font-medium">Unit Price ($)</Label>
          <Input
            id="fulfil_unit_price"
            type="number"
            step="0.01"
            className="h-9 text-xs"
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            placeholder="0.00"
          />
        </div>

        {/* Warranty start & end (Defaults to claim dates) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="fulfil_start" className="text-xs font-medium">
              Warranty Start Date
            </Label>
            <Input
              id="fulfil_start"
              type="date"
              className="h-9 text-xs"
              value={warrantyStartDate}
              onChange={(e) => setWarrantyStartDate(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="fulfil_end" className="text-xs font-medium">
              Warranty End Date
            </Label>
            <Input
              id="fulfil_end"
              type="date"
              className="h-9 text-xs"
              value={warrantyEndDate}
              onChange={(e) => setWarrantyEndDate(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={fulfilMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={fulfilMutation.isPending}>
            {fulfilMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing Fulfilment...
              </>
            ) : (
              "Confirm & Handover"
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

FulfilReplacementForm.propTypes = {
  claim: PropTypes.object.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

const FulfilReplacementModal = ({ open, onOpenChange, claim, onDone }) => {
  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        {open && (
          <FulfilReplacementForm
            key={claim.id || "fulfil"}
            claim={claim}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

FulfilReplacementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  claim: PropTypes.object,
  onDone: PropTypes.func,
};

export default FulfilReplacementModal;
